import { useEffect } from 'react';
import PageLayout from '../../components/feature/PageLayout';
import PageHero from '../../components/feature/PageHero';

export default function ServicesPage() {
  useEffect(() => {
    window.scrollTo(0, 0);

    // SEO Meta Tags
    document.title = 'AI Services | Workshops, Consulting & Audits | Evernorth AI';
    
    const metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription) {
      metaDescription.setAttribute('content', 'Evernorth AI offers AI training workshops, consulting services, and readiness audits for businesses in Northwest Georgia. Practical AI education and implementation support.');
    }

    const canonical = document.querySelector('link[rel="canonical"]');
    if (canonical) {
      canonical.setAttribute('href', `${import.meta.env.VITE_SITE_URL}/services`);
    }

    // Schema.org JSON-LD
    const schemaScript = document.createElement('script');
    schemaScript.type = 'application/ld+json';
    schemaScript.text = JSON.stringify({
      "@context": "https://schema.org",
      "@type": "Service",
      "name": "AI Training and Consulting Services",
      "description": "Comprehensive AI services including workshops, consulting, and readiness audits for businesses",
      "provider": {
        "@type": "LocalBusiness",
        "name": "Evernorth AI",
        "areaServed": "Northwest Georgia",
        "telephone": "+1-706-331-5858"
      },
      "url": `${import.meta.env.VITE_SITE_URL}/services`,
      "serviceType": "AI Training and Consulting",
      "hasOfferCatalog": {
        "@type": "OfferCatalog",
        "name": "AI Services",
        "itemListElement": [
          {
            "@type": "Offer",
            "itemOffered": {
              "@type": "Service",
              "name": "Half Day Workshop",
              "description": "3.5 hours of hands-on AI training"
            }
          },
          {
            "@type": "Offer",
            "itemOffered": {
              "@type": "Service",
              "name": "Full Day Workshop",
              "description": "7 hours of comprehensive AI training"
            }
          },
          {
            "@type": "Offer",
            "itemOffered": {
              "@type": "Service",
              "name": "AI Readiness Audit",
              "description": "Assessment of your organization's AI readiness"
            }
          }
        ]
      }
    });
    document.head.appendChild(schemaScript);

    return () => {
      document.head.removeChild(schemaScript);
    };
  }, []);

  return (
    <PageLayout>
      <PageHero
        badge="What We Offer"
        title="Comprehensive AI Solutions"
        titleHighlight="For Your Business"
        description="From foundational education to advanced support, we provide everything you need to successfully integrate AI into your operations"
      />

      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          {/* How We Help */}
          <div className="text-center mb-20">
            <h2 className="text-4xl md:text-5xl font-bold mb-6 text-navy">
              How We <span className="text-gold">Help</span>
            </h2>
            <p className="text-lg text-charcoal max-w-4xl mx-auto leading-relaxed">
              Evernorth AI helps organizations use AI in sensible, meaningful ways. We focus on training first, then easy AI workflows, then more advanced systems only when a strong foundation is in place.
            </p>
          </div>

          {/* Start Here Section */}
          <div className="mb-20 bg-navy/5 p-10 rounded-2xl border border-navy/10">
            <div className="text-center mb-8">
              <div className="inline-block mb-4 px-4 py-2 bg-gold/20 rounded-md">
                <span className="text-navy text-base font-semibold">Start Here</span>
              </div>
              <h3 className="text-3xl font-bold text-navy mb-4">The Foundations Program</h3>
              <p className="text-lg text-charcoal max-w-3xl mx-auto">
                The Foundations Program builds confidence and clarity for teams new to AI. All workshop attendees leave with an <strong>AI Prompt Reference Guide</strong> and <strong>Demo Examples</strong> from the workshop.
              </p>
            </div>
          </div>

          {/* Workshops */}
          <div className="grid md:grid-cols-2 gap-8 mb-16">
            {/* Half Day Workshop */}
            <div className="group bg-white rounded-2xl border border-soft-gray/40 overflow-hidden hover:border-gold/50 transition-all duration-300 shadow-sm hover:shadow-lg">
              <div className="relative h-64 w-full overflow-hidden">
                <img
                  src="https://qymceftywmiiwdrmhnwb.supabase.co/storage/v1/object/public/files/evernorth-ai/workshops/half-day-workshop.jpg"
                  alt="Half Day Workshop: Practical AI for Your Workplace"
                  title="Half Day AI Workshop for business teams"
                  className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                />
                <div className="absolute top-4 right-4 px-4 py-2 bg-gold rounded-full">
                  <span className="text-white text-sm font-semibold whitespace-nowrap">3.5 Hours</span>
                </div>
              </div>
              <div className="p-8">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-14 h-14 flex items-center justify-center bg-navy/10 rounded-xl group-hover:bg-gold/10 transition-colors">
                    <i className="ri-time-line text-3xl text-navy group-hover:text-gold transition-colors"></i>
                  </div>
                  <div>
                    <div className="text-sm text-gold font-semibold">3.5 Hours</div>
                    <div className="text-xs text-charcoal">Starting at <b>$1,500</b> for 10 attendees</div>
                  </div>
                </div>

                <h3 className="text-2xl font-bold mb-4 text-navy">Half Day Workshop: Practical AI for Your Workplace</h3>
                
                <p className="text-charcoal mb-6 leading-relaxed">
                  A hands-on, interactive workshop that teaches employees how to apply AI to real work, communicate effectively with AI tools, and improve everyday tasks. Designed for organizations that want practical education without committing to a full day.
                </p>

                <div className="mb-6 p-4 bg-gold/10 rounded-lg border border-gold/30">
                  <p className="text-navy font-semibold mb-2">
                    <i className="ri-check-line text-gold mr-2"></i>
                    Attendees leave with skills ready to use that same day.
                  </p>
                  <p className="text-charcoal text-sm">No technical background required. Available virtually or in-person.</p>
                </div>

                <Link
                  to="/workshops/half-day"
                  className="block w-full px-6 py-3 bg-gold text-white font-semibold rounded-lg hover:bg-[#a68a4e] transition-all cursor-pointer whitespace-nowrap text-center"
                >
                  Learn More
                </Link>
              </div>
            </div>

            {/* Full Day Workshop */}
            <div className="group bg-white rounded-2xl border border-soft-gray/40 overflow-hidden hover:border-gold/50 transition-all duration-300 shadow-sm hover:shadow-lg">
              <div className="relative h-64 w-full overflow-hidden">
                <img
                  src="https://qymceftywmiiwdrmhnwb.supabase.co/storage/v1/object/public/files/evernorth-ai/workshops/full-day-workshop.jpg"
                  alt="Full Day Workshop: Transforming Your Work with AI"
                  title="Full Day AI Workshop for business transformation"
                  className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                />
                <div className="absolute top-4 right-4 px-4 py-2 bg-gold rounded-full">
                  <span className="text-white text-sm font-semibold whitespace-nowrap">7 Hours</span>
                </div>
              </div>
              <div className="p-8">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-14 h-14 flex items-center justify-center bg-navy/10 rounded-xl group-hover:bg-gold/10 transition-colors">
                    <i className="ri-calendar-line text-3xl text-navy group-hover:text-gold transition-colors"></i>
                  </div>
                  <div>
                    <div className="text-sm text-gold font-semibold">7 Hours</div>
                    <div className="text-xs text-charcoal">Starting at <b>$3,000</b> for 10 attendees</div>
                  </div>
                </div>

                <h3 className="text-2xl font-bold mb-4 text-navy">Full Day Workshop: Transforming Your Work with AI</h3>
                
                <p className="text-charcoal mb-6 leading-relaxed">
                  Designed for organizations that want to take AI seriously. A full day of clear, practical explanations with deeper discussion, hands-on practice, and space to address questions and concerns. Leaders and staff leave with a common language around AI and greater confidence using it thoughtfully.
                </p>

                <div className="mb-6 p-4 bg-gold/10 rounded-lg border border-gold/30">
                  <p className="text-navy font-semibold mb-2">
                    <i className="ri-check-line text-gold mr-2"></i>
                    Attendees walk out with skills ready to use the next day.
                  </p>
                  <p className="text-charcoal text-sm">No technical background required. Available virtually or in-person.</p>
                </div>

                <Link
                  to="/workshops/full-day"
                  className="block w-full px-6 py-3 bg-gold text-white font-semibold rounded-lg hover:bg-[#a68a4e] transition-all cursor-pointer whitespace-nowrap text-center"
                >
                  Learn More
                </Link>
              </div>
            </div>
          </div>

          {/* We Also Offer Section */}
          <div className="mb-20 relative overflow-hidden bg-gradient-to-br from-navy via-[#0a2a6e] to-[#062299] p-10 rounded-2xl">
            {/* Decorative background elements */}
            <div className="absolute top-0 right-0 w-64 h-64 bg-gold/10 rounded-full -translate-y-1/2 translate-x-1/2 blur-3xl pointer-events-none"></div>
            <div className="absolute bottom-0 left-0 w-48 h-48 bg-white/5 rounded-full translate-y-1/2 -translate-x-1/2 blur-2xl pointer-events-none"></div>

            <div className="relative z-10">
              <div className="text-center mb-10">
                <div className="inline-block mb-4 px-4 py-2 bg-gold/20 border border-gold/30 rounded-md">
                  <span className="text-gold text-base font-semibold">Additional Services</span>
                </div>
                <h3 className="text-3xl font-bold text-white mb-3">We Also Offer</h3>
                <p className="text-white/90 max-w-xl mx-auto text-sm">Expand your AI journey with these specialized services tailored to your organization's needs</p>
              </div>

              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4 mb-10">
                <div className="group flex items-start gap-4 bg-white/10 hover:bg-white/15 border border-white/10 hover:border-gold/40 rounded-xl p-5 transition-all duration-300">
                  <div className="w-10 h-10 flex items-center justify-center bg-gold/20 rounded-lg flex-shrink-0 group-hover:bg-gold/30 transition-colors">
                    <i className="ri-tools-line text-gold text-lg"></i>
                  </div>
                  <span className="text-white/90 text-sm leading-relaxed">Custom Workshops with Industry-Specific Examples + Post-Workshop Support</span>
                </div>

                <div className="group flex items-start gap-4 bg-white/10 hover:bg-white/15 border border-white/10 hover:border-gold/40 rounded-xl p-5 transition-all duration-300">
                  <div className="w-10 h-10 flex items-center justify-center bg-gold/20 rounded-lg flex-shrink-0 group-hover:bg-gold/30 transition-colors">
                    <i className="ri-refresh-line text-gold text-lg"></i>
                  </div>
                  <span className="text-white/90 text-sm leading-relaxed">Follow-Up Sessions</span>
                </div>

                <div className="group flex items-start gap-4 bg-white/10 hover:bg-white/15 border border-white/10 hover:border-gold/40 rounded-xl p-5 transition-all duration-300">
                  <div className="w-10 h-10 flex items-center justify-center bg-gold/20 rounded-lg flex-shrink-0 group-hover:bg-gold/30 transition-colors">
                    <i className="ri-graduation-cap-line text-gold text-lg"></i>
                  </div>
                  <span className="text-white/90 text-sm leading-relaxed">Advanced Trainings</span>
                </div>

                <div className="group flex items-start gap-4 bg-white/10 hover:bg-white/15 border border-white/10 hover:border-gold/40 rounded-xl p-5 transition-all duration-300">
                  <div className="w-10 h-10 flex items-center justify-center bg-gold/20 rounded-lg flex-shrink-0 group-hover:bg-gold/30 transition-colors">
                    <i className="ri-cpu-line text-gold text-lg"></i>
                  </div>
                  <span className="text-white/90 text-sm leading-relaxed">AI Tool &amp; Platform Specific Training and Support</span>
                </div>

                <div className="group flex items-start gap-4 bg-white/10 hover:bg-white/15 border border-white/10 hover:border-gold/40 rounded-xl p-5 transition-all duration-300">
                  <div className="w-10 h-10 flex items-center justify-center bg-gold/20 rounded-lg flex-shrink-0 group-hover:bg-gold/30 transition-colors">
                    <i className="ri-user-star-line text-gold text-lg"></i>
                  </div>
                  <span className="text-white/90 text-sm leading-relaxed">One-on-one AI Accelerator Coaching and Consulting</span>
                </div>

                <div className="group flex items-start gap-4 bg-white/10 hover:bg-white/15 border border-white/10 hover:border-gold/40 rounded-xl p-5 transition-all duration-300 md:col-span-2 lg:col-span-1">
                  <div className="w-10 h-10 flex items-center justify-center bg-gold/20 rounded-lg flex-shrink-0 group-hover:bg-gold/30 transition-colors">
                    <i className="ri-megaphone-line text-gold text-lg"></i>
                  </div>
                  <span className="text-white/90 text-sm leading-relaxed">AI Powered Marketing and Social Media</span>
                </div>
              </div>

              <div className="bg-white/10 backdrop-blur-sm border border-white/20 rounded-xl p-8 text-center">
                <p className="text-lg text-white font-semibold mb-2">
                  Not sure which workshop is right for your team?
                </p>
                <p className="text-white/90 mb-6 text-sm">
                  Schedule a discovery call and we'll help you decide based on your team size, goals, and timeline.
                </p>
                <a
                  href="https://calendly.com/evernorthai/discovery"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-block px-8 py-3 bg-gold text-white font-semibold rounded-full hover:bg-[#a68a4e] transition-all cursor-pointer whitespace-nowrap"
                >
                  Schedule Call
                </a>
              </div>
            </div>
          </div>

          {/* AI Readiness Audit Section */}
          <div className="mb-20">
            <div className="text-center mb-12">
              <div className="inline-block mb-4 px-4 py-2 bg-gold/20 rounded-md">
                <span className="text-navy text-base font-semibold">Next Step</span>
              </div>
              <h3 className="text-3xl md:text-4xl font-bold text-navy mb-4">AI Readiness Audit</h3>
              <p className="text-lg text-charcoal max-w-3xl mx-auto leading-relaxed">
                The AI Readiness Audit helps you understand where AI can improve your operations. We meet with a leader or manager to understand how your office works today.
              </p>
            </div>

            <div className="grid md:grid-cols-2 gap-8 mb-8">
              <div className="bg-white p-8 rounded-2xl border border-soft-gray/40 shadow-sm">
                <h4 className="text-xl font-bold text-navy mb-6">We Review:</h4>
                <div className="space-y-4">
                  <div className="flex items-start gap-3">
                    <i className="ri-checkbox-circle-line text-gold text-xl mt-1"></i>
                    <span className="text-charcoal">Your organization's processes and how you operate on a daily basis</span>
                  </div>
                  <div className="flex items-start gap-3">
                    <i className="ri-checkbox-circle-line text-gold text-xl mt-1"></i>
                    <span className="text-charcoal">Where time is being lost</span>
                  </div>
                  <div className="flex items-start gap-3">
                    <i className="ri-checkbox-circle-line text-gold text-xl mt-1"></i>
                    <span className="text-charcoal">Which tasks repeat daily or weekly</span>
                  </div>
                  <div className="flex items-start gap-3">
                    <i className="ri-checkbox-circle-line text-gold text-xl mt-1"></i>
                    <span className="text-charcoal">Where AI can support clarity, consistency and efficiency</span>
                  </div>
                </div>
              </div>

              <div className="bg-white p-8 rounded-2xl border border-soft-gray/40 shadow-sm">
                <h4 className="text-xl font-bold text-navy mb-6">You Receive:</h4>
                <p className="text-charcoal mb-6 leading-relaxed">
                  A clear summary of strengths and bottlenecks, and what potential AI solutions will improve your business.
                </p>
                <div className="bg-navy/5 p-6 rounded-xl mb-6">
                  <div className="mb-4">
                    <p className="text-sm text-charcoal font-semibold mb-2">Before Audit:</p>
                    <p className="text-charcoal italic">"AI seems overwhelming and we don't know where to begin"</p>
                  </div>
                  <div>
                    <p className="text-sm text-charcoal font-semibold mb-2">After Audit:</p>
                    <p className="text-navy font-semibold">"We have a clear, prioritized plan that makes sense for our team aimed at our goals"</p>
                  </div>
                </div>
                <div className="text-sm text-charcoal mb-4">
                  <i className="ri-global-line text-navy mr-2"></i>
                  We offer this Audit <strong>virtually or in-person</strong>.
                </div>
                <div className="text-gold font-bold text-lg text-center md:text-left">
                  Call to Inquire
                </div>
                <div className="flex flex-wrap gap-3 mt-6 justify-center md:justify-start">
                  <a
                  href="https://calendly.com/evernorthai/discovery"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-block px-8 py-3 bg-gold text-white font-semibold rounded-full hover:bg-[#a68a4e] transition-all cursor-pointer whitespace-nowrap"
                  >
                    Schedule Call
                  </a>
                {/* Audit CTA buttons - temporarily hidden
                  <a
                    href="https://calendly.com/evernorthai/discovery"
                    target="_blank"
                    rel="noopener noreferrer nofollow"
                    className="inline-flex items-center gap-2 px-6 py-3 bg-gold text-white font-semibold rounded-full hover:bg-[#a68a4e] transition-all cursor-pointer whitespace-nowrap"
                  >
                    <i className="ri-video-chat-line text-lg"></i>
                    Schedule Call
                  </a>
                  <a
                    href="https://calendly.com/evernorthai/ai-audit-in-person"
                    target="_blank"
                    rel="noopener noreferrer nofollow"
                    className="inline-flex items-center gap-2 px-6 py-3 bg-navy text-white font-semibold rounded-full hover:bg-navy/90 transition-all cursor-pointer whitespace-nowrap"
                  >
                    <i className="ri-map-pin-line text-lg"></i>
                    Book In-Person Audit
                  </a>
                */}
                </div>
              </div>
            </div>
          </div>

          {/* Essential Workflow Build Section */}
          <div className="mb-20 bg-gradient-to-br from-navy to-[#062299] p-10 rounded-2xl">
            <div className="text-center mb-12">
              <div className="inline-block mb-4 px-4 py-2 bg-white/20 rounded-md">
                <span className="text-white text-base font-semibold">After Training</span>
              </div>
              <h3 className="text-3xl md:text-4xl font-bold text-white mb-4">Essential Workflow Build</h3>
              <p className="text-white/90 max-w-3xl mx-auto leading-relaxed">
                Once your team has gone through training and is comfortable using AI, we help apply those skills to simple workflows.
              </p>
            </div>

            <div className="bg-white/10 backdrop-blur-sm p-8 rounded-2xl border border-white/20 mb-8">
              <h4 className="text-2xl font-bold text-white mb-6">Example Workflow: Customer Communication Support</h4>
              
              <div className="grid md:grid-cols-3 gap-6 mb-6">
                <div className="bg-white/10 p-6 rounded-xl">
                  <div className="text-gold font-semibold mb-3">Before:</div>
                  <p className="text-white/90 text-sm">
                    Staff members spent hours per week manually drafting emails and responses, worrying about tone and clarity.
                  </p>
                </div>
                <div className="bg-white/10 p-6 rounded-xl">
                  <div className="text-gold font-semibold mb-3">After:</div>
                  <p className="text-white/90 text-sm">
                    Staff use their new skills with a prepared prompt template to generate consistent responses, phone scripts, or internal notes in minutes. They review, adjust if needed, and send.
                  </p>
                </div>
                <div className="bg-white/10 p-6 rounded-xl">
                  <div className="text-gold font-semibold mb-3">Result:</div>
                  <p className="text-white/90 text-sm">
                    Faster response times, more professional communication, fewer mistakes and consistency across all staff. This leads to less stress and more time to focus on more important tasks.
                  </p>
                </div>
              </div>
            </div>

            <div className="bg-white p-8 rounded-2xl">
              <h4 className="text-xl font-bold text-navy mb-6">
                Each workflow is designed to match how your office already works. Examples include:
              </h4>
              <div className="grid md:grid-cols-3 gap-4">
                <div className="flex items-start gap-3">
                  <i className="ri-robot-line text-gold text-xl mt-1"></i>
                  <span className="text-charcoal">Custom GPT's - Internal AI Assistants</span>
                </div>
                <div className="flex items-start gap-3">
                  <i className="ri-file-text-line text-gold text-xl mt-1"></i>
                  <span className="text-charcoal">Prepared AI template packages for email, documents, training, hiring, communication and more</span>
                </div>
                <div className="flex items-start gap-3">
                  <i className="ri-global-line text-gold text-xl mt-1"></i>
                  <span className="text-charcoal">New AI-Powered Websites with a Built-In Chatbot</span>
                </div>
              </div>
            </div>
          </div>

          {/* Advanced: Integrated AI Systems Section */}
          <div className="mb-20">
            <div className="text-center mb-12">
              <div className="inline-block mb-4 px-4 py-2 bg-navy/20 rounded-md">
                <span className="text-navy text-base font-semibold">Advanced</span>
              </div>
              <h3 className="text-3xl md:text-4xl font-bold text-navy mb-4">
                Integrated AI Systems <span className="text-charcoal/60">(Later Stage / 2027)</span>
              </h3>
              <p className="text-lg text-charcoal max-w-3xl mx-auto leading-relaxed mb-6">
                For organizations with strong foundations and existing workflows, we offer more connected systems that support communication, documentation, and several internal processes.
              </p>
              <div className="bg-gold/10 border border-gold/30 rounded-xl p-6 max-w-2xl mx-auto">
                <p className="text-navy font-semibold">
                  <i className="ri-information-line text-gold mr-2"></i>
                  This service is only offered after Essential Workflow Build and is scoped carefully to avoid unnecessary complexity.
                </p>
              </div>
            </div>

            <div className="bg-gradient-to-br from-[#f8f8f9] to-white p-10 rounded-2xl border border-soft-gray/30 text-center">
              <i className="ri-calendar-check-line text-5xl text-navy/40 mb-4"></i>
              <h4 className="text-2xl font-bold text-navy mb-4">Offerings and Examples Coming in 2027</h4>
              <p className="text-charcoal text-lg mb-8">
                We're developing comprehensive integrated solutions for organizations ready to take the next step.
              </p>
              <div className="bg-white p-6 rounded-xl border border-soft-gray/30 max-w-md mx-auto">
                <h5 className="text-xl font-bold text-navy mb-4">Take the First Step</h5>
                <p className="text-charcoal mb-6">
                  Schedule a 15 minute call to start the process.
                </p>
                <a
                  href="https://calendly.com/evernorthai/discovery"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-block px-8 py-3 bg-gold text-white font-semibold rounded-full hover:bg-[#a68a4e] transition-all cursor-pointer whitespace-nowrap"
                >
                  Schedule Call
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>
    </PageLayout>
  );
}
